/**
 * Web session store (E10-F01-T04 token + E10-F01-T05 user cache).
 *
 * The api-client auto-injects the `session_token` on every request (acceptance
 * criterion: "из хранилища сессии"). This module is that single source of the
 * token AND the cached authenticated user on the front-end — the legacy SPA kept
 * both in `app.state.session_token` / `app.state.currentUser` + localStorage;
 * here they are two tiny persisted slices with the same guarantee.
 *
 * Two layers, deliberately:
 *   - an in-memory `current` value — the hot path the client reads on every
 *     call (no localStorage hit per request, and works in tests/SSR with no DOM);
 *   - `localStorage` mirror — survives a hard reload so the session is restored
 *     on a cold load (parity with the old token persistence).
 *
 * Every `localStorage` access is wrapped: it can throw (private mode, disabled
 * storage, no DOM) and reading auth state must never crash the app. The cached
 * user is additionally schema-validated on read (`authIdentitySchema`), so a
 * corrupt / tampered / version-skewed blob is treated as "no cache" rather than
 * crashing the restore flow (acceptance: "битый/непарсящийся кэш … без падения").
 *
 * The token is intentionally NOT exported as a React hook here — the auth
 * feature (E10-F01-T05 `useAuth`) owns the login/restore flow and calls
 * `setSession`/`clearSession`; the client only ever *reads* via `getSessionToken`
 * (and `handleUnauthorized` clears via `clearSession`).
 */
import { authIdentitySchema, type AuthIdentity } from '@eer/shared';

const SESSION_KEY = 'eer.session_token';
const USER_KEY = 'eer.user';

let currentToken: string | null = null;
let currentUser: AuthIdentity | null = null;
let tokenHydrated = false;
let userHydrated = false;

function safeGet(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* storage unavailable — value still held in memory for this session */
  }
}

function safeRemove(key: string): void {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    /* storage unavailable — nothing to do */
  }
}

/**
 * The current session token, or `null` when signed out. Lazily hydrates from
 * `localStorage` on first read so a cold load restores the persisted session.
 */
export function getSessionToken(): string | null {
  if (!tokenHydrated) {
    currentToken = safeGet(SESSION_KEY);
    tokenHydrated = true;
  }
  return currentToken;
}

/**
 * The cached authenticated user, or `null` when signed out / no valid cache.
 *
 * Lazily hydrates from `localStorage` on first read. The blob is parsed and
 * validated against `authIdentitySchema`; a missing, non-JSON, or schema-invalid
 * value yields `null` AND drops the broken key (self-healing) so the restore
 * flow falls through to login instead of crashing on a bad cache.
 */
export function getCachedUser(): AuthIdentity | null {
  if (!userHydrated) {
    currentUser = readCachedUser();
    userHydrated = true;
  }
  return currentUser;
}

/** Parse + validate the persisted user blob; drop it on any failure. */
function readCachedUser(): AuthIdentity | null {
  const raw = safeGet(USER_KEY);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Non-JSON / truncated blob — treat as no cache and self-heal.
    safeRemove(USER_KEY);
    return null;
  }
  const result = authIdentitySchema.safeParse(parsed);
  if (!result.success) {
    // Schema-invalid (tampered / older shape) — drop it so restore → login.
    safeRemove(USER_KEY);
    return null;
  }
  return result.data;
}

/**
 * Persist the session (token + user) in memory + localStorage. The single
 * write-path used by both login and a successful restore. A falsy token is
 * ignored (parity with the old `setSessionToken`), so a partial/garbage call
 * never half-establishes a session.
 */
export function setSession(token: string, user: AuthIdentity): void {
  if (!token) return;
  currentToken = token;
  currentUser = user;
  tokenHydrated = true;
  userHydrated = true;
  safeSet(SESSION_KEY, token);
  safeSet(USER_KEY, JSON.stringify(user));
}

/** Persist only the session token (login response carrying just a token). */
export function setSessionToken(token: string): void {
  if (!token) return;
  currentToken = token;
  tokenHydrated = true;
  safeSet(SESSION_KEY, token);
}

/** Persist only the cached user (e.g. after `GET /auth/me` on restore). */
export function setCachedUser(user: AuthIdentity): void {
  currentUser = user;
  userHydrated = true;
  safeSet(USER_KEY, JSON.stringify(user));
}

/**
 * Clear the session everywhere (logout / 401 / handleUnauthorized). Drops BOTH
 * the token and the cached user — the exact set of keys `logout` clears, so the
 * 401 path and the explicit logout leave identical local state.
 */
export function clearSession(): void {
  currentToken = null;
  currentUser = null;
  tokenHydrated = true;
  userHydrated = true;
  safeRemove(SESSION_KEY);
  safeRemove(USER_KEY);
}

/**
 * @deprecated use `clearSession` — kept as an alias so the api-client's existing
 * 401 path (which only knew about the token) clears the user cache too.
 */
export const clearSessionToken = clearSession;

/** Test-only reset of the in-memory hydration latches. */
export function __resetSessionForTests(): void {
  currentToken = null;
  currentUser = null;
  tokenHydrated = false;
  userHydrated = false;
}
